import dayjs from 'dayjs';
import { CONTRACT_TYPE_MASTER_AGREEMENT } from '@/constants/mission';

/** Align with packages/nextjs/lib/utils/extensionAddendumGrace.ts */
export const EXTENSION_ADDENDUM_GRACE_DAYS = 30;

export const EXTENSION_ADDENDUM_CANCEL_REASON_CODE = 'extension_grace_expired';

export const EXTENSION_ADDENDUM_WITHDRAWN_BY_COMPANY_CODE = 'extension_withdrawn_by_company';

export const EXTENSION_ADDENDUM_WITHDRAWN_BY_COMPANY_LEGACY = 'Cancelled by company';

export function isExtensionAddendumWithdrawnByCompany(
  status: string | null | undefined,
  rejectionReason: string | null | undefined
): boolean {
  if (status !== 'cancelled') return false;
  return (
    rejectionReason === EXTENSION_ADDENDUM_WITHDRAWN_BY_COMPANY_CODE ||
    rejectionReason === EXTENSION_ADDENDUM_WITHDRAWN_BY_COMPANY_LEGACY
  );
}

export function isExtensionAddendumCancelledByGrace(
  status: string | null | undefined,
  rejectionReason: string | null | undefined
): boolean {
  if (status !== 'cancelled') return false;
  return rejectionReason === EXTENSION_ADDENDUM_CANCEL_REASON_CODE;
}

function toDateOnly(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  const d = dayjs.isDayjs(value) ? value : dayjs(value);
  if (!d.isValid()) return null;
  return d.format('YYYY-MM-DD');
}

export function getExtensionGraceDeadline(missionEndDate: string | Date | null | undefined): string | null {
  const endOnly = toDateOnly(missionEndDate);
  if (!endOnly) return null;
  return dayjs(endOnly).add(EXTENSION_ADDENDUM_GRACE_DAYS, 'day').format('YYYY-MM-DD');
}

export function isExtensionAddendumInGrace(
  missionEndDate: string | Date | null | undefined,
  today: string = dayjs().format('YYYY-MM-DD')
): boolean {
  const deadline = getExtensionGraceDeadline(missionEndDate);
  if (!deadline) return false;
  return today <= deadline;
}

export function wasExtensionOpenedBeforeMissionEnd(
  addendumCreatedAt: string | Date | null | undefined,
  missionEndDate: string | Date | null | undefined
): boolean {
  const createdOnly = toDateOnly(addendumCreatedAt);
  const endOnly = toDateOnly(missionEndDate);
  if (!createdOnly || !endOnly) return false;
  return createdOnly <= endOnly;
}

const TERMINAL_ADDENDUM_STATUSES = new Set(['rejected', 'cancelled']);

export function isExtensionAddendumPendingStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return !TERMINAL_ADDENDUM_STATUSES.has(status);
}

export interface ExtensionAddendumActionableInput {
  missionEndDate: string | Date | null | undefined;
  addendumCreatedAt: string | Date | null | undefined;
  addendumStatus: string | null | undefined;
  addendumType?: string | null;
}

export function isExtensionAddendumActionable(
  params: ExtensionAddendumActionableInput,
  today: string = dayjs().format('YYYY-MM-DD')
): boolean {
  if (params.addendumType && params.addendumType !== 'extension') return false;
  if (!isExtensionAddendumPendingStatus(params.addendumStatus)) return false;
  if (!wasExtensionOpenedBeforeMissionEnd(params.addendumCreatedAt, params.missionEndDate)) {
    return false;
  }
  return isExtensionAddendumInGrace(params.missionEndDate, today);
}

export function isMasterAgreementBlockingExtension(
  contractType: string | null | undefined,
  maValidUntil: string | Date | null | undefined,
  newEndDate: string | Date | null | undefined
): boolean {
  if (contractType !== CONTRACT_TYPE_MASTER_AGREEMENT || !maValidUntil) return false;

  const validUntil = dayjs(maValidUntil);
  if (!validUntil.isValid()) return false;
  if (validUntil.isBefore(dayjs(), 'day')) return true;

  const newEndOnly = toDateOnly(newEndDate);
  if (!newEndOnly) return false;

  return newEndOnly > validUntil.format('YYYY-MM-DD');
}

export function formatAddendumRejectionReason(
  reason: string | null | undefined,
  t: (key: string) => string
): string | null {
  if (!reason) return null;
  if (reason === EXTENSION_ADDENDUM_CANCEL_REASON_CODE) {
    return t('missionsEdit.addendum.systemCancelReasonGraceExpired');
  }
  if (
    reason === EXTENSION_ADDENDUM_WITHDRAWN_BY_COMPANY_CODE ||
    reason === EXTENSION_ADDENDUM_WITHDRAWN_BY_COMPANY_LEGACY
  ) {
    return t('missionsEdit.addendum.systemCancelReasonWithdrawnByCompany');
  }
  return reason;
}
